import { CheckCircle2, ExternalLink, Check, FileDown, XCircle, Loader2 } from "lucide-react";
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";

import { notificationsSocketService } from "@/modules/dashboard/notifications/services/notifications-socket.service";
import { ColocaIcon } from "@/shared/components/ColocaIcon";
import { ColocaLogo } from "@/shared/components/ColocaLogo";
import { Button } from "@/shared/components/form/Button";
import { useTheme } from "@/shared/theme/useTheme";

import { PAYIN_POLLING_INTERVAL } from "../constants/payins.constants";
import { PaymentsPayinService, PayinRuntimeStatus } from "../services/paymentsPayin.service";
import { generateBrazilPayinPdf } from "../utils/generateBrazilPayinPdf";
import { generateColombiaPayinPdf } from "../utils/generateColombiaPayinPdf";
import type { AdaptedPayinData } from "../utils/payinResponseAdapters";

/** Tipo de estado final del polling */
type PollingFinalStatus = "success" | "failed" | null;

// Runtime payins (`/internal/payments/payins`): CONFIRMED is the paid signal; these are terminal failures.
const RUNTIME_FAILED_STATUSES = new Set<string>([
  PayinRuntimeStatus.REJECTED,
  PayinRuntimeStatus.FAILED,
  PayinRuntimeStatus.REVERSED,
]);

interface PayinSuccessScreenProps {
  payinData: AdaptedPayinData;
  onNewPayin: () => void;
  /** Solo para el flujo de creación (NO historial): mostrar UI amigable para abrir/copy del pay_url en popup. */
  preferPopupForPayUrl?: boolean;
  /** Si es true, al visualizar la respuesta (montaje) se intentará abrir automáticamente el pay_url. */
  autoOpenPayUrl?: boolean;
  /**
   * Opcional: ocultar encabezado de éxito (icono + título) para vistas de solo lectura (historial). Por
   * defecto se muestra.
   */
  hideHeader?: boolean;
  /**
   * Opcional: ocultar el botón de "Crear Nuevo Payin" (o retorno) para vistas de detalle. Por defecto se
   * muestra.
   */
  hideAction?: boolean;
  /**
   * Habilitar polling automático del estado para payins de Brasil. Por defecto es true para el flujo de
   * creación y false para historial.
   */
  enablePolling?: boolean;
  /** Callback cuando el estado del payin cambia (solo para Brasil con polling habilitado) */
  onStatusChange?: (newStatus: string) => void;
  /**
   * Opcional: ocultar la sección "Verificando estado del pago..." cuando se visualiza desde el historial. Por
   * defecto es false (se muestra).
   */
  hidePollingStatus?: boolean;
}

export const PayinSuccessScreen: React.FC<PayinSuccessScreenProps> = ({
  payinData,
  onNewPayin,
  preferPopupForPayUrl = false,
  autoOpenPayUrl = false,
  hideHeader = false,
  hideAction = false,
  enablePolling = true,
  onStatusChange,
  hidePollingStatus = false,
}) => {
  const { theme } = useTheme();
  const [copiedCopiaCola, setCopiedCopiaCola] = useState(false);
  const [copiedPayUrl, setCopiedPayUrl] = useState(false);
  const [didAutoOpenPayUrl, setDidAutoOpenPayUrl] = useState(false);
  const [wasPayUrlPopupBlocked, setWasPayUrlPopupBlocked] = useState(false);
  const [didTryAutoOpenPayUrl, setDidTryAutoOpenPayUrl] = useState(false);
  const [isDownloadingReceipt, setIsDownloadingReceipt] = useState(false);

  // Estado para el polling de Brasil
  const [currentStatus, setCurrentStatus] = useState<string>(payinData?.status || "pending");
  const [pollingFinalStatus, setPollingFinalStatus] = useState<PollingFinalStatus>(null);
  const [isPolling, setIsPolling] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const normalizedPaymentMethod = useMemo(
    () =>
      String(payinData?.payment_method ?? "")
        .toUpperCase()
        .trim(),
    [payinData],
  );
  const isBrazilPayin = Boolean(payinData.amount_brl);
  const isBrebPayin = normalizedPaymentMethod === "BREB";
  // Colombia: si no es Brasil, y tiene pay_url o es BREB, es Colombia
  const isColombiaPayin = !isBrazilPayin && (Boolean(payinData.pay_url) || isBrebPayin);
  const isColombiaBrebPayin = isColombiaPayin && isBrebPayin;
  // Ocultar comisiones para Colombia (incluye BREB) y Brasil
  const shouldHideCommissions = isBrazilPayin || isColombiaPayin;

  // payload_b64 ya es una imagen PNG en base64, solo necesitamos agregar el prefijo
  const payloadB64 = payinData.payload_b64;
  const qrImageSrc = payloadB64
    ? payloadB64.startsWith("data:image/png;base64,")
      ? payloadB64
      : `data:image/png;base64,${payloadB64}`
    : null;

  const isNequiPayin = useMemo(() => normalizedPaymentMethod === "NEQUI", [normalizedPaymentMethod]);

  const openSmallPopup = (url: string) => {
    const width = 420;
    const height = 520;
    const left = Math.max(0, window.screenX + (window.outerWidth - width) / 2);
    const top = Math.max(0, window.screenY + (window.outerHeight - height) / 2);

    const features = [
      "popup=yes",
      `width=${width}`,
      `height=${height}`,
      `left=${left}`,
      `top=${top}`,
      "noopener",
      "noreferrer",
    ].join(",");

    const win = window.open(url, "nequi-payment", features);
    try {
      win?.focus?.();
    } catch {
      // Focusing the popup is best-effort; ignore cross-origin/blocked failures.
    }
    return win;
  };

  // Abrir popup al visualizar la respuesta (montaje). Puede ser bloqueado por el navegador.
  useEffect(() => {
    if (!autoOpenPayUrl) return;
    if (!preferPopupForPayUrl) return;
    if (!isNequiPayin) return;
    if (!payinData.pay_url) return;
    if (didTryAutoOpenPayUrl) return;

    setDidTryAutoOpenPayUrl(true);

    // Dejar que la UI pinte primero (mejor UX y a veces mejora compatibilidad)
    const t = window.setTimeout(() => {
      const win = openSmallPopup(payinData.pay_url);
      if (!win) {
        setWasPayUrlPopupBlocked(true);
      } else {
        setDidAutoOpenPayUrl(true);
      }
    }, 0);

    return () => window.clearTimeout(t);
  }, [autoOpenPayUrl, didTryAutoOpenPayUrl, isNequiPayin, payinData.pay_url, preferPopupForPayUrl]);

  // Determinar si el estado actual es pendiente (necesita polling). Todos los payins que llegan a
  // esta pantalla son del runtime: nacen como CREATED hasta que el webhook del proveedor los asienta.
  const isStatusPending = useCallback((status: string) => {
    const normalizedStatus = status?.toLowerCase?.()?.trim() ?? "";
    return (
      normalizedStatus === "pending" ||
      normalizedStatus === "process" ||
      normalizedStatus === "proccess" ||
      normalizedStatus === "created"
    );
  }, []);

  // Verificar el estado del payin contra el runtime de pagos (estados en mayúsculas).
  const checkPayinStatus = useCallback(async () => {
    if (!payinData?.id) return;

    try {
      const { data: runtimePayin } = await PaymentsPayinService.getPayin(payinData.id);
      const newStatus = runtimePayin.status.toLowerCase();
      const prevStatus = currentStatus?.toLowerCase?.()?.trim?.();

      setCurrentStatus(newStatus);
      if (onStatusChange && newStatus !== prevStatus) {
        onStatusChange(newStatus);
      }

      const isFinal =
        runtimePayin.status === PayinRuntimeStatus.CONFIRMED ||
        RUNTIME_FAILED_STATUSES.has(runtimePayin.status);
      if (isFinal) {
        setPollingFinalStatus(runtimePayin.status === PayinRuntimeStatus.CONFIRMED ? "success" : "failed");
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        setIsPolling(false);
      }
    } catch (error) {
      console.error("Error al verificar estado del payin:", error);
      // No detener el polling en caso de error temporal
    }
  }, [payinData?.id, currentStatus, onStatusChange]);

  // El mismo chequeo, disparado por el socket en vez del reloj: el runtime anuncia `payin:updated`
  // apenas el webhook del proveedor liquida el cobro, y el polling de abajo queda como respaldo.
  useEffect(() => {
    if (!payinData?.id) return;
    return notificationsSocketService.onPayinUpdate((payload) => {
      if (payload.id === payinData.id) void checkPayinStatus();
    });
  }, [payinData?.id, checkPayinStatus]);

  // Efecto para iniciar el polling cuando es un payin de Brasil con estado pendiente
  useEffect(() => {
    // Solo activar polling si:
    // 1. Es un payin de Brasil
    // 2. El polling está habilitado
    // 3. El estado actual es pendiente
    // 4. No hay un estado final ya definido
    if (!isBrazilPayin || !enablePolling || !isStatusPending(currentStatus) || pollingFinalStatus) {
      return;
    }

    // Verificar que el payin tiene un ID válido
    if (!payinData?.id) {
      return;
    }

    setIsPolling(true);

    // Ejecutar primera verificación inmediatamente
    checkPayinStatus();

    // Iniciar polling cada 5 segundos
    pollingIntervalRef.current = setInterval(() => {
      checkPayinStatus();
    }, PAYIN_POLLING_INTERVAL);

    // Cleanup: detener el polling al desmontar
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setIsPolling(false);
    };
  }, [
    isBrazilPayin,
    enablePolling,
    currentStatus,
    pollingFinalStatus,
    payinData?.id,
    checkPayinStatus,
    isStatusPending,
  ]);

  // Función para copiar el Copia e Cola al portapapeles
  const handleCopyCopiaCola = async () => {
    const copiaCola = payinData.copia_e_cola;
    if (!copiaCola) return;

    try {
      await navigator.clipboard.writeText(copiaCola);
      setCopiedCopiaCola(true);
      setTimeout(() => setCopiedCopiaCola(false), 2000);
    } catch (err) {
      console.error("Error al copiar:", err);
    }
  };

  const handleCopyPayUrl = async () => {
    if (!payinData.pay_url) return;
    try {
      await navigator.clipboard.writeText(payinData.pay_url);
      setCopiedPayUrl(true);
      setTimeout(() => setCopiedPayUrl(false), 2000);
    } catch (err) {
      console.error("Error al copiar URL:", err);
    }
  };

  const handleOpenPayUrlPopup = () => {
    if (!payinData.pay_url) return;
    const win = openSmallPopup(payinData.pay_url);
    if (!win) setWasPayUrlPopupBlocked(true);
  };

  const handleDownloadColombiaReceipt = async () => {
    if (!isColombiaPayin) return;
    setIsDownloadingReceipt(true);
    try {
      await generateColombiaPayinPdf(payinData);
    } catch (error) {
      console.error("Error generando comprobante de payin Colombia", error);
    } finally {
      setIsDownloadingReceipt(false);
    }
  };

  const handleDownloadBrazilReceipt = async () => {
    if (!isBrazilPayin) return;
    setIsDownloadingReceipt(true);
    try {
      // Consultar el estado actual del payin antes de generar el PDF
      let updatedStatus = payinData.status;
      let updatedAmountReceived = payinData.amount_received;
      let updatedCreatedAt = payinData.created_at;

      try {
        // Fresh status/net/created_at from the payments runtime (the initiate response carries
        // no timestamps, so the adapted created_at is a client-side stamp).
        const { data: runtimePayin } = await PaymentsPayinService.getPayin(payinData.id);
        updatedStatus = runtimePayin.status.toLowerCase();
        if (runtimePayin.net_amount != null) {
          updatedAmountReceived = Number(runtimePayin.net_amount);
        }
        if (runtimePayin.created_at) {
          updatedCreatedAt = runtimePayin.created_at;
        }
      } catch (statusError) {
        console.warn("No se pudo obtener estado actualizado, usando datos en cache:", statusError);
      }

      // Generar el PDF con los datos actualizados. La comisión se omite: el runtime solo reporta
      // el total (fee_total) y el desglose porcentaje/fija del PDF sería engañoso.
      await generateBrazilPayinPdf({
        id: payinData.id,
        status: updatedStatus,
        amountBRL: payinData.amount_brl ?? payinData.amount,
        amountReceived: updatedAmountReceived,
        copiaECola: payinData.copia_e_cola,
        payloadB64: payinData.payload_b64,
        createdAt: updatedCreatedAt,
        updatedAt: payinData.updated_at,
      });
    } catch (error) {
      console.error("Error generando comprobante de payin Brasil", error);
    } finally {
      setIsDownloadingReceipt(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    // Si es solo una fecha (sin hora), formatear diferente
    if (dateString.includes("T")) {
      return date.toLocaleString("es-CO", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } else {
      return date.toLocaleDateString("es-CO", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Estados exitosos que deben mostrarse en verde claro
  const SUCCESS_STATUSES = ["success", "approved", "complete"];

  // Estados pendientes/en proceso que deben mostrarse en color naranja claro
  const PENDING_STATUSES = ["pending", "process", "proccess"];

  // Estados de error que deben mostrarse en rojo claro
  const ERROR_STATUSES = ["failed", "rejected", "error", "expired"];

  // Estados creados que deben mostrarse en azul claro
  const CREATED_STATUSES = ["created"];

  // Función para normalizar y formatear el estado (primera letra mayúscula, resto minúsculas)
  const formatStatus = (status: string): string => {
    if (!status) return "";
    const normalized = status.toLowerCase().trim();
    if (normalized.length === 0) return "";
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  // Función para obtener la clase CSS del badge según el estado
  // Todos los colores usan el mismo tono claro (100 para fondo, 800 para texto) para mantener consistencia
  const getStatusBadgeClass = (status: string) => {
    const statusLower = status?.toLowerCase?.()?.trim() ?? "";

    // Estados exitosos: success, approved, complete (en cualquier caso) - Verde claro
    if (SUCCESS_STATUSES.includes(statusLower)) {
      return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
    }

    // Estados pendientes/en proceso: pending, process (en cualquier caso) - Naranja claro
    if (PENDING_STATUSES.includes(statusLower)) {
      return "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400";
    }

    // Estados de error: failed, rejected, error (en cualquier caso) - Rojo claro
    if (ERROR_STATUSES.includes(statusLower)) {
      return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
    }

    // Estados creados: created (en cualquier caso) - Azul claro
    if (CREATED_STATUSES.includes(statusLower)) {
      return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
    }

    // Estados completados (diferentes de 'complete') - Verde claro
    if (statusLower === "completed") {
      return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
    }

    // Estado por defecto - Gris claro
    return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
  };

  const formatCurrencyCOP = (amount: number) => {
    // Formato del número sin código de moneda (el símbolo se muestra separado con color verde)
    return new Intl.NumberFormat("es-CO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatCurrencyBRL = (amount: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatExpiration = (dateString?: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const pad = (val: number) => String(val).padStart(2, "0");
    return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${String(date.getFullYear()).slice(
      -2,
    )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  if (isBrazilPayin) {
    return (
      <div className="max-w-md mx-auto space-y-4">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-700 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-zinc-800">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">PIX con Código QR</h2>
            <ColocaLogo variant={theme === "dark" ? "white" : "black"} className="h-10 max-w-48" />
          </div>

          <div className="px-4 pt-6 pb-4 text-center space-y-2">
            <div className="flex justify-center mb-1">
              <ColocaIcon variant={theme === "dark" ? "white" : "black"} size="40" />
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300">Información de pago</p>
            <p className="text-base font-semibold text-gray-900 dark:text-white">
              Valor a pagar:{" "}
              <span className="text-green-600 dark:text-green-400 text-xl">
                {formatCurrencyBRL(payinData.amount_brl ?? payinData.amount)}
              </span>
            </p>
            {payinData.amount_received !== undefined && payinData.amount_received !== null && (
              <p className="text-base font-semibold text-gray-900 dark:text-white">
                Monto a recibir:{" "}
                <span className="text-green-600 dark:text-green-400 text-xl">
                  {formatCurrencyBRL(payinData.amount_received)}
                </span>
              </p>
            )}
            {payinData.expired_at && (
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Fecha límite de pago:{" "}
                <span className="font-semibold">{formatExpiration(payinData.expired_at)}</span>
              </p>
            )}
            <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">Elige una forma de pago:</p>
          </div>

          <div className="px-4 pb-5 space-y-4">
            {/* Mensaje de pago exitoso */}
            {pollingFinalStatus === "success" && (
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 p-6 space-y-4">
                <div className="flex flex-col items-center justify-center space-y-3">
                  <div className="w-16 h-16 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="h-10 w-10 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="text-xl font-bold text-green-700 dark:text-green-400">¡Pago Exitoso!</h3>
                  <p className="text-sm text-green-600 dark:text-green-300 text-center">
                    Tu pago ha sido procesado correctamente.
                  </p>
                </div>
              </div>
            )}

            {/* Mensaje de pago fallido */}
            {pollingFinalStatus === "failed" && (
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 p-6 space-y-4">
                <div className="flex flex-col items-center justify-center space-y-3">
                  <div className="w-16 h-16 bg-red-100 dark:bg-red-900/40 rounded-full flex items-center justify-center">
                    <XCircle className="h-10 w-10 text-red-600 dark:text-red-400" />
                  </div>
                  <h3 className="text-xl font-bold text-red-700 dark:text-red-400">Pago Fallido</h3>
                  <p className="text-sm text-red-600 dark:text-red-300 text-center">
                    No se pudo procesar tu pago.
                  </p>
                </div>
              </div>
            )}

            {/* Indicador de estado del polling (cuando está en proceso) */}
            {!hidePollingStatus && isPolling && !pollingFinalStatus && (
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-3">
                <div className="flex items-center justify-center gap-2 text-blue-600 dark:text-blue-400">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm font-medium">Verificando estado del pago...</span>
                </div>
              </div>
            )}

            {/* Copia e Cola - Solo mostrar si no hay estado final */}
            {!pollingFinalStatus && payinData.copia_e_cola && (
              <div className="bg-gray-50 dark:bg-zinc-800 rounded-lg border border-gray-200 dark:border-zinc-700 p-4 space-y-3">
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                  Copia y pega el código (copia e cola o ID)
                </p>
                <ol className="text-xs text-gray-600 dark:text-gray-300 space-y-1 list-decimal list-inside">
                  <li>Copia el código de la transacción</li>
                  <li>Pega el código en tu app bancaria para identificar el pago PIX.</li>
                </ol>
                <button
                  onClick={handleCopyCopiaCola}
                  className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                >
                  {copiedCopiaCola ? (
                    <>
                      <Check className="h-4 w-4" />
                      <span>Código copiado</span>
                    </>
                  ) : (
                    <span>Copiar código</span>
                  )}
                </button>
              </div>
            )}

            {/* QR - Solo mostrar si no hay estado final */}
            {!pollingFinalStatus && qrImageSrc && (
              <div className="bg-gray-50 dark:bg-zinc-800 rounded-lg border border-gray-200 dark:border-zinc-700 p-4 space-y-3">
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 text-center">
                  O escanea el código QR con tu app bancaria
                </p>
                <div className="w-full max-w-sm mx-auto">
                  <img
                    src={qrImageSrc}
                    alt="Código QR de pago"
                    className="w-full aspect-square bg-white dark:bg-zinc-900 rounded-lg"
                  />
                </div>
                <p className="text-[11px] text-gray-600 dark:text-gray-300 text-center leading-relaxed">
                  El pago puede tardar hasta un minuto en confirmarse. Permanece en esta página hasta que
                  finalice el proceso. Serás redirigido automáticamente a la configuración del servicio.
                </p>
              </div>
            )}

            {/* Descargar comprobante PDF */}
            <Button
              variant="primary"
              onClick={handleDownloadBrazilReceipt}
              isLoading={isDownloadingReceipt}
              className="w-full"
              icon={<FileDown className="h-4 w-4" />}
            >
              Descargar comprobante PDF
            </Button>
          </div>
        </div>

        {/* Comisiones debajo del QR - Ocultar para Brasil, México y Colombia */}
        {!shouldHideCommissions && (
          <div className="bg-white dark:bg-zinc-900 rounded-lg border border-gray-200 dark:border-zinc-800 p-4 space-y-2 shadow-sm">
            <h3 className="text-md font-semibold text-gray-900 dark:text-white">Comisiones</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <span className="text-xs text-gray-600 dark:text-gray-400">Porcentaje:</span>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {payinData.commission.percentage}%
                </p>
              </div>
              <div>
                <span className="text-xs text-gray-600 dark:text-gray-400">Tarifa fija:</span>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(parseFloat(payinData.commission.fee_fix))}
                </p>
              </div>
              <div>
                <span className="text-xs text-gray-600 dark:text-gray-400">Total comisión:</span>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(parseFloat(payinData.commission.total_fee))}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Fecha de creación */}
        <div className="bg-white dark:bg-zinc-900 rounded-lg border border-gray-200 dark:border-zinc-800 p-4 shadow-sm">
          <div>
            <span className="text-xs text-gray-600 dark:text-gray-400">Creado:</span>
            <p className="text-sm text-gray-900 dark:text-white">{formatDate(payinData.created_at)}</p>
          </div>
        </div>

        {/* Botón crear nuevo payin - solo para Brasil cuando no está en modo historial */}
        {!hideAction && (
          <div className="pt-4">
            <Button onClick={onNewPayin} className="w-full">
              Crear Nuevo Payin
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Success header (opcional) */}
      {!hideHeader && (
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <CheckCircle2 className="h-16 w-16 text-green-500" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Payin Creado Exitosamente</h2>
          <p className="text-gray-600 dark:text-gray-400">
            El payin ha sido creado correctamente. Aquí están los detalles:
          </p>
        </div>
      )}

      {/* Payin details card */}
      <div className="bg-white dark:bg-zinc-800 rounded-lg border border-gray-200 dark:border-zinc-700 shadow-sm p-6 space-y-6">
        {/* Logo ColocaPayments */}
        <div className="flex justify-center pb-4 border-b border-gray-200 dark:border-zinc-700">
          <ColocaLogo variant={theme === "dark" ? "white" : "black"} className="max-w-48" />
        </div>

        {/* Status badge */}
        <div className="flex items-center justify-between gap-3 pb-4 border-b border-gray-200 dark:border-zinc-700">
          <div>
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Estado:</span>
            <span
              className={`ml-2 px-3 py-1 rounded-full text-sm font-semibold ${getStatusBadgeClass(payinData.status)}`}
            >
              {formatStatus(payinData.status)}
            </span>
          </div>
          {isColombiaPayin && (
            <Button
              variant="primary"
              onClick={handleDownloadColombiaReceipt}
              isLoading={isDownloadingReceipt}
              icon={<FileDown className="h-4 w-4" />}
            >
              Descargar comprobante PDF
            </Button>
          )}
          {isBrazilPayin && (
            <Button
              variant="secondary"
              onClick={handleDownloadBrazilReceipt}
              isLoading={isDownloadingReceipt}
            >
              Descargar comprobante PDF
            </Button>
          )}
        </div>

        {/* Main information grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Payer Information - Solo mostrar si hay datos del pagador */}
          {(payinData.payer_name || payinData.payer_email) && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-zinc-700 pb-2">
                Información del Pagador
              </h3>
              <div className="space-y-3">
                {payinData.payer_name && (
                  <div>
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Nombre:</span>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">{payinData.payer_name}</p>
                  </div>
                )}
                {payinData.payer_document && (
                  <div>
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Documento:</span>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">
                      {payinData.payer_document_type && `${payinData.payer_document_type} - `}
                      {payinData.payer_document}
                    </p>
                  </div>
                )}
                {payinData.payer_email && (
                  <div>
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Email:</span>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">{payinData.payer_email}</p>
                  </div>
                )}
                {(() => {
                  if (isColombiaBrebPayin) {
                    if (!payinData.pay_key) return null;
                    return (
                      <div>
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Llave Bre-b:
                        </span>
                        <p className="text-sm text-gray-900 dark:text-white mt-1">{payinData.pay_key}</p>
                      </div>
                    );
                  }

                  if (!payinData.payer_phone_number) return null;
                  return (
                    <div>
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Teléfono:</span>
                      <p className="text-sm text-gray-900 dark:text-white mt-1">
                        {payinData.payer_phone_number}
                      </p>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Payment Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-zinc-700 pb-2">
              Información del Pago
            </h3>
            <div className="space-y-3">
              {payinData.payment_method && (
                <div>
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Método de pago:
                  </span>
                  <p className="text-sm text-gray-900 dark:text-white mt-1">
                    {payinData.payment_method.toLowerCase() === "pse"
                      ? "pse"
                      : payinData.payment_method.toUpperCase()}
                  </p>
                </div>
              )}
              {/* Solo mostrar Monto si NO es un payin de Brasil */}
              {!payinData.amount_brl && (
                <div>
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Monto:</span>
                  <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                    {isColombiaPayin ? (
                      <>
                        <span className="text-green-600 dark:text-green-400">$</span>{" "}
                        {formatCurrencyCOP(payinData.amount)}
                      </>
                    ) : (
                      formatCurrency(payinData.amount)
                    )}
                  </p>
                </div>
              )}
              {/* Solo mostrar Monto a recibir si NO es un payin de Brasil */}
              {!payinData.amount_brl &&
                payinData.amount_received !== undefined &&
                payinData.amount_received !== null && (
                  <div>
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Monto a recibir:
                    </span>
                    <p className="text-lg font-bold text-green-600 dark:text-green-400 mt-1">
                      {isColombiaPayin ? (
                        <>
                          <span className="text-green-600 dark:text-green-400">$</span>{" "}
                          {formatCurrencyCOP(payinData.amount_received)}
                        </>
                      ) : (
                        formatCurrency(payinData.amount_received)
                      )}
                    </p>
                  </div>
                )}
              {/* Campos específicos de Brasil */}
              {payinData.copia_e_cola && (
                <div>
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2 block">
                    Copia el ID de la transacción
                  </span>
                  <button
                    onClick={handleCopyCopiaCola}
                    className="w-full bg-green-500 hover:bg-green-600 text-white font-medium py-4 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                  >
                    {copiedCopiaCola ? (
                      <>
                        <Check className="h-5 w-5" />
                        <span>Copiado!</span>
                      </>
                    ) : (
                      <span>Copiar ID</span>
                    )}
                  </button>
                </div>
              )}
              {qrImageSrc && (
                <div>
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Código QR:</span>
                  <div className="mt-2 w-full max-w-sm mx-auto">
                    <img
                      src={qrImageSrc}
                      alt="Código QR de pago"
                      className="w-full aspect-square bg-white dark:bg-zinc-900 rounded-lg"
                    />
                  </div>
                </div>
              )}
              {payinData.amount_brl && (
                <div>
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Valor a pagar: BRL
                  </span>
                  <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                    {formatCurrency(payinData.amount_brl)}
                  </p>
                </div>
              )}
              {payinData.expired_at && (
                <div>
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Fecha de expiración:
                  </span>
                  <p className="text-sm text-gray-900 dark:text-white mt-1">
                    {formatDate(payinData.expired_at)}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Commission Information - Ocultar para Colombia y Brasil */}
        {!shouldHideCommissions && (
          <div className="bg-gray-50 dark:bg-zinc-900 rounded-lg p-4 space-y-2">
            <h3 className="text-md font-semibold text-gray-900 dark:text-white">Comisiones</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <span className="text-xs text-gray-600 dark:text-gray-400">Porcentaje:</span>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {payinData.commission.percentage}%
                </p>
              </div>
              <div>
                <span className="text-xs text-gray-600 dark:text-gray-400">Tarifa fija:</span>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(parseFloat(payinData.commission.fee_fix))}
                </p>
              </div>
              <div>
                <span className="text-xs text-gray-600 dark:text-gray-400">Total comisión:</span>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(parseFloat(payinData.commission.total_fee))}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* NEQUI - UI amigable (2 botones) */}
        {preferPopupForPayUrl && isNequiPayin && payinData.pay_url && (
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800 space-y-3">
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Abre tu pago</span>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                {wasPayUrlPopupBlocked
                  ? "Tu navegador bloqueó la ventana emergente. Haz clic en “Abrir pago”."
                  : didAutoOpenPayUrl
                    ? "Se abrió una ventana emergente para procesar el pago. Si no la ves, puedes abrirla de nuevo."
                    : autoOpenPayUrl
                      ? "Abriendo ventana emergente..."
                      : "Puedes abrir el pago en una ventana emergente."}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="primary"
                onClick={handleOpenPayUrlPopup}
                type="button"
                className="w-full"
                icon={<ExternalLink className="h-4 w-4" />}
              >
                Abrir pago
              </Button>
              <Button
                variant="secondary"
                onClick={handleCopyPayUrl}
                type="button"
                className="w-full"
                icon={copiedPayUrl ? <Check className="h-4 w-4" /> : undefined}
              >
                {copiedPayUrl ? "Enlace copiado" : "Copiar enlace"}
              </Button>
            </div>
          </div>
        )}

        {/* Payment URL */}
        {payinData.pay_url && !(preferPopupForPayUrl && isNequiPayin) && (
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">URL de pago:</span>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 break-all">{payinData.pay_url}</p>
              </div>
              <a
                href={payinData.pay_url}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-4 flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                <span className="text-sm">Abrir</span>
              </a>
            </div>
          </div>
        )}

        {/* Timestamps */}
        <div className="pt-4 border-t border-gray-200 dark:border-zinc-700">
          <div>
            <span className="text-xs text-gray-600 dark:text-gray-400">Creado:</span>
            <p className="text-sm text-gray-900 dark:text-white">{formatDate(payinData.created_at)}</p>
          </div>
        </div>
      </div>

      {/* Action button */}
      {!hideAction && (
        <div className="pt-4">
          <Button onClick={onNewPayin} className="w-full">
            Crear Nuevo Payin
          </Button>
        </div>
      )}
    </div>
  );
};
